import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import BlogArticleBody from '../components/blog/BlogArticleBody';
import SafeIcon from '../common/SafeIcon';
import { getBlogImageForPost } from '../data/blogImages.js';
import { fallbackBlogPosts, getRebuiltFallbackPostBySlug } from '../data/fallbackBlogPosts';
import { fetchBlogPostBySlug, isStrapiConfigured } from '../lib/strapiBlog';

const { FiArrowLeft, FiCalendar, FiClock, FiUser } = FiIcons;

const normalizeSources = (sources) => {
  if (!Array.isArray(sources)) return [];

  return sources
    .map((source) => {
      if (typeof source === 'string') {
        return {
          title: source,
          url: source.startsWith('http') ? source : '',
        };
      }

      return {
        title: source?.title || source?.name || source?.url || 'Source',
        url: source?.url || '',
      };
    })
    .filter((source) => source.title);
};

const BlogPost = () => {
  const { slug } = useParams();
  const fallbackPost = useMemo(
    () => fallbackBlogPosts.find((item) => item.slug === slug),
    [slug]
  );
  const [post, setPost] = useState(fallbackPost || null);
  const [isLoading, setIsLoading] = useState(isStrapiConfigured && !fallbackPost);
  const sourceLinks = useMemo(() => normalizeSources(post?.sources), [post?.sources]);
  const articleSchema = useMemo(() => {
    if (!post) return null;

    return {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: post.title,
      description: post.seoDescription || post.excerpt,
      image: post.image,
      datePublished: post.publishedAt,
      author: {
        '@type': 'Person',
        name: post.author,
      },
      publisher: {
        '@type': 'Organization',
        name: 'KCG',
        url: 'https://kcg.care',
      },
      mainEntityOfPage: `https://kcg.care/blog/${post.slug}`,
      citation: sourceLinks.map((source) => source.url).filter(Boolean),
    };
  }, [post, sourceLinks]);

  useEffect(() => {
    let mounted = true;

    const loadPost = async () => {
      if (!isStrapiConfigured) return;

      try {
        const strapiPost = await fetchBlogPostBySlug(slug);
        if (mounted && strapiPost) {
          setPost(getRebuiltFallbackPostBySlug(strapiPost.slug) || strapiPost);
        }
      } catch (error) {
        console.warn('Unable to load Strapi blog post. Falling back to local content.', error);
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    setPost(fallbackPost || null);
    setIsLoading(isStrapiConfigured && !fallbackPost);
    loadPost();

    return () => {
      mounted = false;
    };
  }, [fallbackPost, slug]);

  if (isLoading) {
    return (
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-5 w-32 bg-gray-200 rounded animate-pulse mb-10" />
          <div className="h-12 bg-gray-200 rounded animate-pulse mb-6" />
          <div className="h-6 w-3/4 bg-gray-100 rounded animate-pulse mb-10" />
          <div className="h-96 bg-gray-200 rounded-2xl animate-pulse" />
        </div>
      </section>
    );
  }

  if (!post) {
    return (
      <>
        <Helmet>
          <title>Article Not Found - KCG</title>
        </Helmet>
        <section className="py-24 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Article Not Found</h1>
            <p className="text-lg text-gray-600 mb-8">
              The article you are looking for may have moved or is no longer available.
            </p>
            <Link
              to="/blog"
              className="inline-flex items-center bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-2" />
              Back to Blog
            </Link>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{post.seoTitle || post.title} - KCG</title>
        <meta name="description" content={post.seoDescription || post.excerpt} />
        <meta property="og:title" content={post.seoTitle || post.title} />
        <meta property="og:description" content={post.seoDescription || post.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={post.image} />
        {articleSchema && (
          <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        )}
      </Helmet>

      <article>
        <section className="bg-gradient-to-br from-blue-50 to-white py-16 lg:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link
              to="/blog"
              className="inline-flex items-center text-blue-700 hover:text-blue-800 font-semibold mb-8"
            >
              <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-2" />
              Back to Blog
            </Link>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <span className="inline-block bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full mb-5">
                {post.category}
              </span>
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
                {post.title}
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed mb-8">{post.excerpt}</p>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-600">
                <span className="inline-flex items-center">
                  <SafeIcon icon={FiUser} className="w-4 h-4 mr-2" />
                  {post.author}
                </span>
                <span className="inline-flex items-center">
                  <SafeIcon icon={FiCalendar} className="w-4 h-4 mr-2" />
                  {post.date}
                </span>
                <span className="inline-flex items-center">
                  <SafeIcon icon={FiClock} className="w-4 h-4 mr-2" />
                  {post.readTime}
                </span>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="bg-white pb-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.img
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              src={post.image}
              alt={post.imageAlt || post.title}
              onError={(event) => {
                event.currentTarget.onerror = null;
                event.currentTarget.src = getBlogImageForPost(post.category, post.title);
              }}
              className="w-full h-72 md:h-[28rem] object-cover rounded-2xl shadow-lg -mt-10 mb-12"
            />
            <div className="max-w-3xl mx-auto">
              <BlogArticleBody content={post.body} />
              {sourceLinks.length > 0 && (
                <aside className="mt-12 rounded-2xl border border-blue-100 bg-blue-50 p-6">
                  <h2 className="text-xl font-bold text-blue-950 mb-4">Sources Checked</h2>
                  <ul className="space-y-3 text-sm text-blue-950">
                    {sourceLinks.map((source) => (
                      <li key={`${source.title}-${source.url}`}>
                        {source.url ? (
                          <a
                            href={source.url}
                            className="font-medium underline underline-offset-4 hover:text-blue-700"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {source.title}
                          </a>
                        ) : (
                          source.title
                        )}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-4 text-sm text-blue-900">
                    These links support general education only. Care decisions may still need advice
                    from adult social care, CQC, a GP, pharmacist or another regulated
                    professional.
                  </p>
                </aside>
              )}
            </div>
          </div>
        </section>
      </article>
    </>
  );
};

export default BlogPost;
